import React, { useRef, useState } from "react";
import { Form, Button, Badge, Table } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "react-toastify";
import { TEXT_ROLE_OPTIONS, ALIGNMENT_OPTIONS, FONT_WEIGHT_OPTIONS, CATEGORY_TEXT_ROLES, DATE_FORMAT_OPTIONS, FORMAT_ROLES } from "../../helpers/tmConstants";
import { blankTextStyle } from "../../helpers/textStyles";

const roleLabel = (role) => (TEXT_ROLE_OPTIONS.find((o) => o.value === role) || {}).label || role;
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
// TTF only — any other font format is rejected with a clear error.
const FONT_FILE_ACCEPT = ".ttf";

/** Basename of a font URL/filename, for a compact display label. */
const fontFileLabel = (entry) => {
  if (entry.font_file instanceof File) return entry.font_file.name;
  if (entry.font_url) {
    try {
      return decodeURIComponent(entry.font_url.split("/").pop());
    } catch {
      return entry.font_url.split("/").pop();
    }
  }
  return "";
};

/** One table row for a single role's style overrides. */
const TextStyleRow = React.memo(React.forwardRef(function TextStyleRow({ entry, isDefault, disabled, onChange, onPatch, onRemove, dragHandleProps, style, ...draggableProps }, ref) {
  const fileInputRef = useRef(null);
  const set = (field) => (e) => onChange(entry.role, field, e.target.value === "" ? null : e.target.value);
  const setNumber = (field) => (e) => onChange(entry.role, field, e.target.value === "" ? null : Number(e.target.value));
  // Opacity is a 0–1 ratio — clamp so the value can never leave that range,
  // even if the user types past the spinner's min/max (browsers don't
  // enforce that on typed input, only on the up/down arrows).
  const setOpacity = (e) => {
    if (e.target.value === "") return onChange(entry.role, "opacity", null);
    const n = Number(e.target.value);
    if (Number.isNaN(n)) return;
    onChange(entry.role, "opacity", Math.min(1, Math.max(0, n)));
  };

  const onFontFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "ttf") {
      toast.error(`"${file.name}" is not a TTF file. Only .ttf fonts are allowed.`, { position: "bottom-right", autoClose: 2000 });
      e.target.value = "";
      return;
    }
    onChange(entry.role, "font_file", file);
  };
  const clearFontFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    onPatch(entry.role, { font_file: null, font_url: null });
  };

  const fileLabel = fontFileLabel(entry);
  const supportsFormat = FORMAT_ROLES.includes(entry.role);

  return (
    <tr ref={ref} style={style} {...draggableProps}>
      <td className="align-middle text-center" style={{ width: 36 }}>
        <span {...dragHandleProps} title="Drag to reorder" style={{ cursor: "grab", display: "inline-flex", alignItems: "center", color: "#888" }}>
          <i className="bx bx-menu fs-5"></i>
        </span>
      </td>
      <td className="align-middle text-nowrap">
        <span className="text-capitalize">{roleLabel(entry.role)}</span>{" "}
        {isDefault && <Badge bg="secondary">Default</Badge>}
      </td>
      <td className="align-middle">
        <Form.Control size="sm" style={{ minWidth: 140 }} value={entry.font_name || ""} onChange={set("font_name")} placeholder="Font name" disabled={disabled} />
      </td>
      <td className="align-middle">
        <div className="d-flex align-items-center gap-1" style={{ minWidth: 180 }}>
          <Button variant="outline-secondary" size="sm" className="text-nowrap" onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={disabled} title="TTF files only">
            <i className="bx bx-upload"></i> {fileLabel ? "Change" : "Upload TTF"}
          </Button>
          {fileLabel && (
            <>
              <span className="small text-truncate" style={{ maxWidth: 100 }} title={fileLabel}>{fileLabel}</span>
              <Button variant="link" size="sm" className="p-0 text-danger" onClick={clearFontFile} disabled={disabled} title="Remove font file">
                <i className="bx bx-x"></i>
              </Button>
            </>
          )}
          <Form.Control ref={fileInputRef} type="file" accept={FONT_FILE_ACCEPT} onChange={onFontFileSelect} disabled={disabled} hidden />
        </div>
      </td>
      <td className="align-middle">
        <Form.Control size="sm" type="number" min={0} style={{ minWidth: 80 }} value={entry.font_size ?? ""} onChange={setNumber("font_size")} placeholder="px" disabled={disabled} />
      </td>
      <td className="align-middle">
        <Form.Control
          size="sm"
          type="color"
          style={{ width: 44, padding: 2 }}
          value={HEX_RE.test(entry.color || "") ? entry.color : "#000000"}
          onChange={set("color")}
          title={entry.color || "Pick a color"}
          disabled={disabled}
        />
      </td>
      <td className="align-middle">
        <Form.Select size="sm" style={{ minWidth: 100 }} value={entry.align || ""} onChange={set("align")} disabled={disabled}>
          <option value="">Default</option>
          {ALIGNMENT_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </Form.Select>
      </td>
      <td className="align-middle">
        <Form.Select size="sm" style={{ minWidth: 110 }} value={entry.weight || ""} onChange={set("weight")} disabled={disabled}>
          <option value="">Default</option>
          {FONT_WEIGHT_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </Form.Select>
      </td>
      <td className="align-middle">
        <Form.Control size="sm" type="number" step={0.1} style={{ minWidth: 80 }} value={entry.tracking ?? ""} onChange={setNumber("tracking")} placeholder="0" disabled={disabled} />
      </td>
      <td className="align-middle">
        <Form.Control size="sm" type="number" min={0} max={1} step={0.01} style={{ minWidth: 80 }} value={entry.opacity ?? ""} onChange={setOpacity} placeholder="0–1" disabled={disabled} />
      </td>
      <td className="align-middle">
        {supportsFormat ? (
          <Form.Select size="sm" style={{ minWidth: 160 }} value={entry.format || ""} onChange={set("format")} disabled={disabled}>
            <option value="">Default</option>
            {DATE_FORMAT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label} ({o.example})</option>
            ))}
          </Form.Select>
        ) : (
          <span className="text-muted small">—</span>
        )}
      </td>
      <td className="align-middle">
        <Form.Control size="sm" style={{ minWidth: 120 }} value={entry.text || ""} onChange={set("text")} placeholder="e.g. label text" disabled={disabled} />
      </td>
      <td className="align-middle text-center">
        {onRemove && (
          <Button
            variant="outline-danger"
            onClick={() => onRemove(entry.role)}
            disabled={disabled}
            title="Remove style"
            className="d-inline-flex align-items-center justify-content-center p-0"
            style={{ width: 30, height: 30 }}
          >
            <i className="bx bx-trash-alt"></i>
          </Button>
        )}
      </td>
    </tr>
  );
}));

/**
 * TextStylesEditor — per-role typography table, one row per role.
 *
 * Props:
 *  - value    : array of { role, font_name, font_url, font_file, font_size, color, align, weight, tracking, opacity }
 *  - category : parent widget's category_name (drives default-role badges)
 *  - onChange : (newArray) => void
 *  - disabled : disable every input + Add/Remove
 */
const TextStylesEditor = ({ value = [], category, onChange, disabled = false }) => {
  const [picking, setPicking] = useState(false);
  const [pickedRole, setPickedRole] = useState("");
  const [customRole, setCustomRole] = useState("");

  const defaultRoles = new Set(CATEGORY_TEXT_ROLES[category] || []);
  const usedRoles = new Set(value.map((v) => v.role));
  const availableOptions = TEXT_ROLE_OPTIONS.filter((o) => !usedRoles.has(o.value));

  const patchRole = (role, patch) => {
    onChange(value.map((v) => (v.role === role ? { ...v, ...patch } : v)));
  };
  const updateRole = (role, field, val) => patchRole(role, { [field]: val });
  const removeRole = (role) => {
    onChange(value.filter((v) => v.role !== role));
  };
  const confirmAdd = () => {
    const role = pickedRole === "__custom__" ? customRole.trim() : pickedRole;
    if (!role || usedRoles.has(role)) return;
    onChange([...value, blankTextStyle(role)]);
    setPicking(false);
    setPickedRole("");
    setCustomRole("");
  };

  const onDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(value);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onChange(reordered);
  };

  return (
    <div className="mt-2">
      <h6 className="text-muted mb-2" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Text Styles
      </h6>
      <p className="text-muted small mb-2">
        Configure per-text role typography for this widget variant. Existing Font Name/Text Color are used as fallback.
      </p>

      <div>
        {value.length === 0 && !picking && (
          <p className="text-muted small mb-2">No text styles yet. Pick a category to auto-generate roles, or add one below.</p>
        )}

        {value.length > 0 && (
          <div
            className="mb-2 border rounded-3"
            style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}
          >
            <Table bordered size="sm" className="align-middle mb-0" style={{ minWidth: 1420, marginBottom: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th className="text-nowrap">Role</th>
                  <th className="text-nowrap">Font Name</th>
                  <th className="text-nowrap">Font File</th>
                  <th className="text-nowrap">Font Size</th>
                  <th className="text-nowrap">Color</th>
                  <th className="text-nowrap">Alignment</th>
                  <th className="text-nowrap">Weight</th>
                  <th className="text-nowrap">Tracking</th>
                  <th className="text-nowrap">Opacity</th>
                  <th className="text-nowrap">Format</th>
                  <th className="text-nowrap">Text</th>
                  <th className="text-nowrap">Actions</th>
                </tr>
              </thead>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="text-styles-roles">
                  {(provided) => (
                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                      {value.map((entry, index) => (
                        <Draggable key={entry.role} draggableId={entry.role} index={index} isDragDisabled={disabled}>
                          {(dragProvided) => (
                            <TextStyleRow
                              entry={entry}
                              isDefault={defaultRoles.has(entry.role)}
                              disabled={disabled}
                              onChange={updateRole}
                              onPatch={patchRole}
                              onRemove={removeRole}
                              dragHandleProps={dragProvided.dragHandleProps}
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </div>
        )}

        {picking ? (
          <div className="d-flex align-items-end gap-2 flex-wrap">
            <Form.Group>
              <Form.Label className="mb-1" style={{ fontSize: "0.85rem" }}>Role</Form.Label>
              <Form.Select value={pickedRole} onChange={(e) => setPickedRole(e.target.value)} disabled={disabled}>
                <option value="">Select role</option>
                {availableOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                <option value="__custom__">Custom…</option>
              </Form.Select>
            </Form.Group>
            {pickedRole === "__custom__" && (
              <Form.Group>
                <Form.Label className="mb-1" style={{ fontSize: "0.85rem" }}>Custom role name</Form.Label>
                <Form.Control value={customRole} onChange={(e) => setCustomRole(e.target.value)} placeholder="e.g. subtitle" disabled={disabled} />
              </Form.Group>
            )}
            <Button variant="primary" size="sm" onClick={confirmAdd} disabled={disabled || !pickedRole || (pickedRole === "__custom__" && !customRole.trim())}>
              Add
            </Button>
            <Button variant="secondary" size="sm" onClick={() => { setPicking(false); setPickedRole(""); setCustomRole(""); }} disabled={disabled}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="outline-primary" size="sm" onClick={() => setPicking(true)} disabled={disabled}>
            <i className="bx bx-plus"></i> Add Text Style
          </Button>
        )}
      </div>
    </div>
  );
};

export default TextStylesEditor;
